import { Button, Kind, Size } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import {
  BrameCampaign,
  useDeleteBrameCampaignMutation,
  useGetBrameCampaignsQuery,
} from 'api/brameCampaigns'
import { landingPageThemeApi } from 'api/landingPageTheme'
import Help from 'components/Help'
import Spinner from 'components/Spinner'
import { buildTranslate } from 'locales'
import React, { useState } from 'react'
import { Col, Row } from 'react-bootstrap-five'
import { useSelector } from 'react-redux'
import { selectApp } from 'selectors/app'
import { history } from 'store'
import BrameSetupNotice from './BrameSetupNotice'
import EmptyGameState from './EmptyGameState'
import GameCardList from './GameCardList'
import GameDeleteModal from './GameDeleteModal'

const t = buildTranslate('games')

interface GameCollectionContentProps {
  onAddGame: () => void
  onEditGame: (game: BrameCampaign) => void
}

const GameCollectionContent = ({
  onAddGame,
  onEditGame,
}: GameCollectionContentProps) => {
  const { data: games = [], isLoading, error } = useGetBrameCampaignsQuery()
  const [deleteBrameCampaign, { isLoading: isDeleting }] =
    useDeleteBrameCampaignMutation()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [gameToDelete, setGameToDelete] = useState<BrameCampaign | null>(null)

  const app = useSelector(selectApp)
  const appId = app?.id

  const { data: landingPageThemeData } =
    landingPageThemeApi.useGetLandingPageThemeQuery(appId, {
      skip: !appId,
      refetchOnMountOrArgChange: true,
    })

  const landingPageTheme = landingPageThemeData?.app_landing_page_theme ?? null

  const findExistingGameBlock = (brameCampaignId: string) => {
    if (!landingPageTheme?.content_blocks) return null

    return landingPageTheme.content_blocks.find(
      block =>
        block.type === 'game' &&
        block.settings?.brame_campaign_id === brameCampaignId
    )
  }

  const getGameHomepageStatus = (gameId: string) => {
    const existingBlock = findExistingGameBlock(gameId)

    return {
      exists: !!existingBlock,
      blockId: existingBlock?.id,
    }
  }

  const handleMenuAction = (gameId: string, action: string) => {
    const game = games.find(game => game.id === gameId)
    if (!game) return

    if (action === 'edit') {
      onEditGame(game)
    } else if (action === 'delete') {
      setGameToDelete(game)
      setShowDeleteModal(true)
    }
  }

  const triggerDelete = async () => {
    if (gameToDelete) {
      try {
        await deleteBrameCampaign(gameToDelete.id).unwrap()
        setShowDeleteModal(false)
        setGameToDelete(null)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to delete game:', error)
      }
    }
  }

  const handleAddToHomepage = (gameId: string) => {
    const { exists, blockId } = getGameHomepageStatus(gameId)

    const queryParam =
      exists && blockId
        ? `blockId=${encodeURIComponent(blockId.toString())}`
        : `gameId=${encodeURIComponent(gameId)}`

    const targetUrl = `/brand_content#app_homepage_content?${queryParam}`
    history.push(targetUrl)
  }

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center p-4">
        <Spinner isLoading={true} />
      </div>
    )
  }

  if (error) {
    // Don't return early on error - show the error message but also render the static content
  }

  return (
    <div>
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <Text.Header3 className="mb-2">
                {t('enabled.collection.title')}
              </Text.Header3>
              <Text.BodyText3 className="text-muted">
                {t('enabled.collection.description')}
              </Text.BodyText3>
            </div>
            <Button
              kind={Kind.PRIMARY}
              size={Size.STANDARD}
              className="ml-4"
              onClick={onAddGame}
            >
              {t('enabled.collection.addGameButton')}
            </Button>
          </div>
        </Col>
      </Row>

      <BrameSetupNotice />

      <Row className="mb-4">
        <Col>
          {!error && games.length > 0 ? (
            <GameCardList
              games={games}
              onAddToHomepage={handleAddToHomepage}
              onMenuAction={handleMenuAction}
              getGameHomepageStatus={getGameHomepageStatus}
            />
          ) : (
            <EmptyGameState />
          )}
        </Col>
      </Row>

      <Row className="mt-4">
        <Col>
          <Button
            kind={Kind.SECONDARY}
            size={Size.STANDARD}
            className="w-100 d-flex align-items-center justify-content-center"
            onClick={onAddGame}
          >
            {t('enabled.collection.addGameButton')}{' '}
            <span className="ml-2">+</span>
          </Button>
        </Col>
      </Row>

      <Row className="mt-5">
        <Col>
          <Help
            title="Help"
            articles={[
              {
                title:
                  'Learn more about configuring games in Thanx (via Brame)',
                url: 'https://help.thanx.com/hc/en-us/articles/37613433406615-Games-Powered-by-Brame',
              },
            ]}
          />
        </Col>
      </Row>

      <GameDeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        triggerDelete={triggerDelete}
        isLoading={isDeleting}
        gameName={gameToDelete?.name || ''}
      />
    </div>
  )
}

export default GameCollectionContent
